import React, { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

const rand = (min, max) => Math.random() * (max - min) + min;

// Theme-aware animated ambient backdrop. Colors are driven by the
// [data-business] attribute on <html> (set by applyTheme), so switching
// business instantly re-themes the embers / glow / smoke.
export function AmbientBackground() {
  const { selectedBusinessId } = useAuth();
  const isRuby = selectedBusinessId === "rubys";

  const embers = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => ({
        id: i,
        left: rand(2, 98),
        size: rand(3, 7),
        duration: rand(7, 15),
        delay: -rand(0, 15),
        drift: `${rand(-40, 40)}px`,
        gold: Math.random() < 0.45,
      })),
    []
  );

  const sparks = useMemo(
    () =>
      Array.from({ length: isRuby ? 12 : 22 }).map((_, i) => ({
        id: i,
        top: rand(2, 92),
        left: rand(2, 98),
        size: rand(2, 4),
        duration: rand(2.4, 5.5),
        delay: -rand(0, 5),
      })),
    [isRuby]
  );

  const smoke = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => ({
        id: i,
        left: rand(0, 90),
        size: rand(130, 240),
        duration: rand(16, 28),
        delay: -rand(0, 24),
        x: `${rand(-50, 90)}px`,
      })),
    []
  );

  return (
    <div className="ambient" aria-hidden="true" data-testid="ambient-background">
      <div className="ambient__base" />
      <div className="ambient__glow g1" style={{ width: 420, height: 420, top: "-6%", left: "-8%" }} />
      <div className="ambient__glow g2" style={{ width: 360, height: 360, top: "8%", right: "-6%" }} />
      <div className="ambient__glow g3" style={{ width: 520, height: 520, bottom: "-16%", left: "30%" }} />

      {isRuby &&
        smoke.map((s) => (
          <div
            key={`sm-${s.id}`}
            className="ambient__smoke"
            style={{
              left: `${s.left}%`,
              bottom: "-10%",
              width: s.size,
              height: s.size,
              animationDuration: `${s.duration}s`,
              animationDelay: `${s.delay}s`,
              "--smoke-x": s.x,
            }}
          />
        ))}

      {embers.map((e) => (
        <div
          key={`em-${e.id}`}
          className={`ambient__ember${e.gold ? " gold" : ""}`}
          style={{
            left: `${e.left}%`,
            width: e.size,
            height: e.size,
            animationDuration: `${e.duration}s`,
            animationDelay: `${e.delay}s`,
            "--drift": e.drift,
          }}
        />
      ))}

      {sparks.map((s) => (
        <div
          key={`sp-${s.id}`}
          className="ambient__spark"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
